import type { UIMessage } from 'ai'
import { isToolUIPart } from 'ai'
import { Copy, Check, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  Message,
  MessageContent,
  MessageActions,
  MessageAction,
  MessageResponse,
} from '@/components/ai-elements/message'
import {
  Reasoning,
  ReasoningTrigger,
  ReasoningContent,
} from '@/components/ai-elements/reasoning'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { AgentChip } from './agent-chip'
import { PolicyCard } from './policy-card'
import { ClarificationMarkers } from './clarification-markers'
import { CardRenderer } from './card-renderer'
import type { CardOutput } from './card-renderer'

interface ChatMessagesProps {
  messages: UIMessage[]
  isStreaming: boolean
  currentAgent?: { agentName: string; action?: string } | null
  onSendMessage?: (text: string) => void
}

/** Supervisor routing tools -- their output messages should render inline */
const TRANSFER_TOOLS = new Set([
  'transfer_to_general_chat',
  'transfer_to_attributes_manager',
  'transfer_to_policy_crafter',
  'transfer_to_identity_inspector',
  'transfer_to_discovery_analyst',
  'transfer_to_compliance_auditor',
])

/** Tools that return structured card data — suppress text that duplicates the card */
const CARD_TOOLS = new Set([
  'create_policy_draft',
  'create_attribute',
  'search_attributes',
  'search_policy_drafts',
  'get_policy_draft',
  'deploy_policy',
  'undeploy_policy',
  'search_subjects',
  'lookup_subject',
  'get_subject_detail',
  'get_subject_attributes',
  'search_subjects_by_attribute',
  'set_subject_attribute',
  'list_discovery_connections',
  'get_discovery_connection',
  'start_discovery_scan',
  'get_scan_results',
  'reclassify_scan_data',
  'list_scans',
  'query_audit_logs',
  'query_decision_logs',
  'get_decision_stats',
  'get_infrastructure_status',
  'list_applications',
])

/** Human-friendly labels for tools shown during in-progress execution */
const TOOL_WORKING_LABELS: Record<string, string> = {
  transfer_to_policy_crafter: 'Policy Crafter is working',
  transfer_to_attributes_manager: 'Attributes Manager is working',
  transfer_to_general_chat: 'Thinking',
  transfer_to_identity_inspector: 'Identity Inspector is working',
  transfer_to_discovery_analyst: 'Discovery Analyst is working',
  transfer_to_compliance_auditor: 'Compliance Auditor is working',
  create_policy_draft: 'Creating policy draft',
  create_attribute: 'Creating attribute',
  search_attributes: 'Searching attributes',
  search_policy_drafts: 'Searching policies',
  get_policy_draft: 'Loading policy',
  deploy_policy: 'Deploying policy',
  undeploy_policy: 'Undeploying policy',
  build_xacml_rule: 'Building policy rule',
  build_xacml_target: 'Building policy target',
  build_xacml_obligation: 'Building obligation',
  build_xacml_condition: 'Building condition',
  preview_xacml_policy: 'Generating policy preview',
  validate_xacml_policy: 'Validating policy',
  search_subjects: 'Searching subjects',
  lookup_subject: 'Looking up subject',
  get_subject_detail: 'Loading subject profile',
  get_subject_attributes: 'Loading subject attributes',
  get_attribute_history: 'Loading attribute history',
  set_subject_attribute: 'Updating subject attribute',
  search_subjects_by_attribute: 'Searching by attribute',
  list_discovery_connections: 'Listing connections',
  get_discovery_connection: 'Loading connection details',
  test_discovery_connection: 'Testing connection',
  start_discovery_scan: 'Starting scan',
  get_scan_status: 'Checking scan progress',
  get_scan_results: 'Loading scan results',
  reclassify_scan_data: 'Reclassifying data',
  list_scans: 'Loading scan history',
  query_audit_logs: 'Querying audit trail',
  query_decision_logs: 'Querying decisions',
  get_decision_stats: 'Loading decision stats',
  get_dashboard_metrics: 'Loading platform metrics',
  get_infrastructure_status: 'Checking infrastructure',
  list_applications: 'Loading applications',
  get_application_detail: 'Loading application details',
  test_application_connection: 'Testing connection',
}

function getToolName(part: UIMessage['parts'][number]): string | null {
  const p = part as { type: string; toolName?: string }
  if (p.type === 'dynamic-tool') return p.toolName ?? null
  if (p.type.startsWith('tool-')) return p.type.slice(5)
  return null
}

/** Check if a tool output is a structured card object */
function getCardOutput(output: unknown): CardOutput | null {
  if (output && typeof output === 'object' && '__cardType' in output) {
    return output as CardOutput
  }
  return null
}

/** Card types where the accompanying text just duplicates the card content */
const TEXT_SUPPRESSING_CARD_TYPES = new Set([
  'policy-status-change',
])

/**
 * Check whether a message contains card tool outputs that warrant text suppression.
 * Preview cards do NOT suppress text because the AI's explanation is valuable context.
 */
function messageHasCardOutputs(parts: UIMessage['parts']): boolean {
  for (const part of parts) {
    if (!isToolUIPart(part)) continue
    const toolName = getToolName(part)
    if (!toolName) continue

    // Check direct card tools — only suppress text for non-preview card types
    if (CARD_TOOLS.has(toolName)) {
      const output = (part as { output?: unknown }).output
      const card = getCardOutput(output)
      if (card && TEXT_SUPPRESSING_CARD_TYPES.has(card.__cardType)) return true
    }
  }
  return false
}

export function ChatMessages({ messages, isStreaming, currentAgent, onSendMessage }: ChatMessagesProps) {
  const lastMessage = messages[messages.length - 1]
  const showThinking = isStreaming && lastMessage?.role === 'user'

  return (
    <div className="space-y-4">
      {messages.map((message, idx) => {
        const isLastAssistant = isStreaming && idx === messages.length - 1 && message.role === 'assistant'
        return (
          <div key={message.id}>
            {isLastAssistant && currentAgent && (
              <div className="mb-1.5 ml-1">
                <AgentChip agentName={currentAgent.agentName} />
              </div>
            )}
            <MessageItem
              message={message}
              isLast={idx === messages.length - 1}
              isStreaming={isStreaming && idx === messages.length - 1}
              onSendMessage={onSendMessage}
            />
          </div>
        )
      })}
      {showThinking && (
        <Message from="assistant">
          <MessageContent>
            {currentAgent && (
              <div className="mb-1.5">
                <AgentChip agentName={currentAgent.agentName} />
              </div>
            )}
            <Shimmer className="text-sm">Thinking...</Shimmer>
          </MessageContent>
        </Message>
      )}
    </div>
  )
}

function MessageItem({
  message,
  isLast: _isLast,
  isStreaming,
  onSendMessage,
}: {
  message: UIMessage
  isLast: boolean
  isStreaming: boolean
  onSendMessage?: (text: string) => void
}) {
  const isUser = message.role === 'user'
  const [copied, setCopied] = useState(false)

  // When cards are present in this message, suppress text to avoid duplication
  const suppressText = !isUser && !isStreaming && messageHasCardOutputs(message.parts)

  const handleCopy = () => {
    const text = collectVisibleText(message.parts)
    navigator.clipboard.writeText(text)
    toast.success('Copied')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Message from={message.role}>
      <MessageContent>
        {message.parts.map((part, i) => (
          <MessagePartRenderer
            key={i}
            part={part}
            isStreaming={isStreaming}
            suppressText={suppressText}
            onSendMessage={onSendMessage}
          />
        ))}
      </MessageContent>
      {!isUser && !isStreaming && (
        <MessageActions>
          <MessageAction tooltip="Copy" onClick={handleCopy}>
            {copied ? (
              <Check className="size-3.5 text-green-500" />
            ) : (
              <Copy className="size-3.5" />
            )}
          </MessageAction>
        </MessageActions>
      )}
    </Message>
  )
}

/**
 * Collect all visible text from message parts, including nested sub-agent
 * output from transfer tools, for copy-to-clipboard.
 */
function collectVisibleText(parts: UIMessage['parts']): string {
  const texts: string[] = []
  for (const part of parts) {
    if (part.type === 'text') {
      texts.push(part.text)
    } else if (isToolUIPart(part)) {
      const output = (part as { output?: unknown }).output
      if (Array.isArray(output)) {
        for (const msg of output) {
          if (msg && typeof msg === 'object' && 'parts' in msg) {
            texts.push(collectVisibleText(msg.parts))
          }
        }
      }
    }
  }
  return texts.filter(Boolean).join('\n')
}

function MessagePartRenderer({
  part,
  isStreaming,
  suppressText = false,
  onSendMessage,
}: {
  part: UIMessage['parts'][number]
  isStreaming: boolean
  suppressText?: boolean
  onSendMessage?: (text: string) => void
}) {
  // Text parts — suppress when cards are shown to avoid duplicate info
  if (part.type === 'text') {
    if (!part.text || suppressText) return null
    return (
      <>
        <MessageResponse>{part.text}</MessageResponse>
        {isStreaming && <span className="inline-block w-1.5 h-4 ml-0.5 bg-foreground/70 align-text-bottom animate-blink" />}
      </>
    )
  }

  // Reasoning
  if (part.type === 'reasoning') {
    return (
      <Reasoning isStreaming={isStreaming}>
        <ReasoningTrigger />
        <ReasoningContent>{part.text}</ReasoningContent>
      </Reasoning>
    )
  }

  // Tool invocations (type is "tool-<name>" or "dynamic-tool" in AI SDK v6)
  if (isToolUIPart(part)) {
    const toolName = getToolName(part)
    const output = (part as { output?: unknown }).output

    // Supervisor transfer tools: render sub-agent messages directly, no tool UI
    if (toolName && TRANSFER_TOOLS.has(toolName)) {
      if (!output && isStreaming) {
        return <ToolWorkingIndicator toolName={toolName} />
      }
      return <ToolOutputMessages output={output} isStreaming={isStreaming} onSendMessage={onSendMessage} />
    }

    // Check for structured card outputs from domain tools
    const cardOutput = getCardOutput(output)
    if (cardOutput) {
      return <CardRenderer card={cardOutput} onSendMessage={onSendMessage} />
    }

    // Tool is in-progress (no output yet) during streaming — show working indicator
    if (!output && isStreaming && toolName) {
      return <ToolWorkingIndicator toolName={toolName} />
    }

    // Legacy: surface blocked create/write tool messages (string outputs)
    const WRITE_TOOLS = ['create_attribute', 'create_policy_draft']
    if (toolName && WRITE_TOOLS.includes(toolName)) {
      const result = typeof output === 'string' ? output : ''
      if (result.toLowerCase().includes('blocked') || result.toLowerCase().includes('denied') || result.toLowerCase().includes('not approved')) {
        return (
          <div className="my-1 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            {result}
          </div>
        )
      }
    }
    return null
  }

  // Step-start markers -- skip
  if (part.type === 'step-start') return null

  // Data parts (custom artifacts - legacy support)
  const anyPart = part as { type: string; data?: unknown }

  if (anyPart.type === 'data-xacml') {
    const data = anyPart.data as {
      content: Record<string, unknown>
      humanReadable?: string
    }
    return (
      <PolicyCard
        data={{
          id: '',
          name: 'XACML Policy',
          status: 'DRAFT',
          humanReadable: data.humanReadable,
          xacmlPolicy: data.content,
        }}
      />
    )
  }

  if (anyPart.type === 'data-clarification') {
    const data = anyPart.data as {
      markers: Array<{
        field: string
        reason: string
        suggestedQuestions?: string[]
      }>
    }
    return <ClarificationMarkers markers={data.markers} />
  }

  return null
}

/** Animated indicator shown while a tool is executing (no output yet) */
function ToolWorkingIndicator({ toolName }: { toolName: string }) {
  const label = TOOL_WORKING_LABELS[toolName] ?? 'Working'
  const isAgent = TRANSFER_TOOLS.has(toolName)

  return (
    <div className="flex items-center gap-2 py-2">
      <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
      {isAgent ? (
        <Shimmer className="text-sm font-medium">{`${label}...`}</Shimmer>
      ) : (
        <span className="text-sm text-muted-foreground animate-pulse">{`${label}...`}</span>
      )}
    </div>
  )
}

/**
 * Renders sub-agent output messages from a supervisor transfer tool.
 * The output is an array of UIMessage objects; we render their parts
 * inline as if they were direct assistant messages.
 */
function ToolOutputMessages({
  output,
  isStreaming,
  onSendMessage,
}: {
  output: unknown
  isStreaming: boolean
  onSendMessage?: (text: string) => void
}) {
  if (!output) return null

  // Output can be a single message object or an array of messages
  const messages: unknown[] = Array.isArray(output) ? output : [output]

  return (
    <>
      {messages.map((msg, msgIdx) => {
        if (!msg || typeof msg !== 'object' || !('parts' in msg)) return null
        const uiMsg = msg as { parts: UIMessage['parts'] }
        return uiMsg.parts.map((innerPart, partIdx) => (
          <MessagePartRenderer
            key={`${msgIdx}-${partIdx}`}
            part={innerPart}
            isStreaming={isStreaming}
            suppressText={false}
            onSendMessage={onSendMessage}
          />
        ))
      })}
    </>
  )
}
